import { Question } from "@/types/quiz";

/**
 * Automatically categorizes a question's difficulty based on content analysis
 * Rules:
 * - Hard: Question contains newlines (multiple lines)
 * - Medium: Any option has more than 3 words
 * - Easy: All other questions
 */
export function categorizeQuestionDifficulty(question: Question): "easy" | "medium" | "hard" {
  // Check if question has newlines (indicator of hard difficulty)
  if (question.question.includes('\n')) {
    return "hard";
  }
  
  // Count words in each option
  const optionWordCounts = question.options.map(option => {
    // Handle null/undefined options
    if (!option) return 0;
    // Count words by splitting on whitespace
    const words = option.trim().split(/\s+/);
    return words.length;
  });
  
  // Check if any option has more than 3 words (indicator of medium difficulty)
  if (optionWordCounts.some(count => count > 3)) {
    return "medium";
  }
  
  // Default to easy difficulty
  return "easy";
}

/**
 * Updates a question's difficulty property based on content analysis
 * if the difficulty is not already set
 */
export function ensureQuestionDifficulty(question: Question): Question {
  if (!question.difficulty) {
    return {
      ...question,
      difficulty: categorizeQuestionDifficulty(question)
    };
  }
  return question;
}

/**
 * Batch processes an array of questions to ensure all have difficulty set
 */
export function processQuestionsWithDifficulty(questions: Question[]): Question[] {
  if (!Array.isArray(questions)) {
    console.error("processQuestionsWithDifficulty received non-array:", questions);
    return [];
  }
  
  return questions.map(q => {
    // Create a new object to avoid modifying the original
    const processedQuestion = { ...q };
    
    // Set difficulty if not already set
    if (!processedQuestion.difficulty) {
      const questionLength = processedQuestion.question?.length || 0;
      const optionsLength = processedQuestion.options?.reduce((sum, option) => sum + (option?.length || 0), 0) || 0;
      
      // Simple algorithm based on content length
      if (questionLength > 150 || optionsLength > 250) {
        processedQuestion.difficulty = 'hard';
      } else if (questionLength > 80 || optionsLength > 150) {
        processedQuestion.difficulty = 'medium';
      } else {
        processedQuestion.difficulty = 'easy';
      }
    }
    
    // Ensure other required properties are set
    processedQuestion.question_type = processedQuestion.question_type || 'mcq';
    
    return processedQuestion;
  });
}

/**
 * Creates a complete frozen copy of a question to avoid any reference issues
 * This ensures we capture the exact state of a question when it's displayed
 */
export function createFrozenQuestion(question: Question): Question {
  // Deep copy the question using JSON parse/stringify to break all references
  const frozenQuestion = JSON.parse(JSON.stringify(question));
  
  // Ensure all required properties are set with default values if missing
  return {
    ...frozenQuestion,
    id: frozenQuestion.id || Math.random().toString(36).substring(2),
    difficulty: frozenQuestion.difficulty || 'medium',
    category: frozenQuestion.category || 'General',
    subject: frozenQuestion.subject || 'General',
    question_type: frozenQuestion.question_type || 'mcq',
    // Preserve any custom properties that might have been added
    shownAtIndex: frozenQuestion.shownAtIndex,
    userAnswer: frozenQuestion.userAnswer
  };
}

/**
 * Safely converts a value to a valid Question object
 * This is used when parsing question data from APIs or storage
 */
export function ensureValidQuestion(questionData: any): Question | null {
  if (!questionData || typeof questionData !== 'object') {
    console.error('Invalid question data:', questionData);
    return null;
  }
  
  try {
    // Create a base question with default values
    const validatedQuestion: Question = {
      id: questionData.id || 0,
      question: questionData.question || 'Missing question text',
      options: Array.isArray(questionData.options) ? [...questionData.options] : ['No options available'],
      correctanswer: typeof questionData.correctanswer === 'number' ? questionData.correctanswer : 0,
      difficulty: (questionData.difficulty || 'medium') as "easy" | "medium" | "hard",
      category: questionData.category || 'General',
      subject: questionData.subject || 'General',
      question_type: questionData.question_type || 'mcq'
    };
    
    // Add optional fields if present
    if (questionData.explanation) {
      validatedQuestion.explanation = questionData.explanation;
    }
    
    if (questionData.hint) {
      validatedQuestion.hint = questionData.hint;
    }
    
    if (typeof questionData.ai_generated === 'boolean') {
      validatedQuestion.ai_generated = questionData.ai_generated;
    }
    
    if (typeof questionData.time_limit === 'number') {
      validatedQuestion.time_limit = questionData.time_limit;
    }
    
    // Include any custom properties if present
    if (typeof questionData.shownAtIndex === 'number') {
      validatedQuestion.shownAtIndex = questionData.shownAtIndex;
    }
    
    if (questionData.userAnswer !== undefined) {
      validatedQuestion.userAnswer = questionData.userAnswer;
    }
    
    return validatedQuestion;
  } catch (error) {
    console.error('Error validating question data:', error);
    return null;
  }
}
